<template>
  <div
    id="performance"
    class="px-4"
  >
    <v-row
      key="performancePortalAdvancedFilter"
      align="center"
      dense
      class="mt-0 mb-3"
    >
      <v-spacer />
      <v-col cols="auto">
        <v-btn
          color="primary"
          text
          class="text-none"
          @click="filtersDialog = true"
        >
          {{ $t('button.filters') }}
        </v-btn>
      </v-col>
      <v-col
        v-if="$vuetify.breakpoint.xlOnly"
        cols="auto"
      >
        <v-btn
          v-for="view in views"
          :key="view.name"
          :value="view.name"
          :outlined="currentView !== view.name"
          :color="currentView === view.name ? 'primary' : ''"
          :text="currentView !== view.name"
          class="mx-1"
          @click="currentView = view.name"
        >
          {{ view.title }}
        </v-btn>
      </v-col>

      <v-col
        v-else
        cols="auto"
      >
        <v-select
          v-model="currentView"
          solo
          dense
          hide-details
          color="primary"
          item-value="name"
          item-text="title"
          :items="views"
          style="width: 150px"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          icon
          @click="customizeModal = !customizeModal"
        >
          <v-icon
            small
          >
            fa-light fa-gear
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-divider />

    <v-container
      v-if="variables.loading"
      class="d-flex justify-center align-center my-16"
    >
      <v-progress-circular
        indeterminate
        size="75"
      />
    </v-container>
    <div v-else>
      <v-row
        align="center"
        class="mt-2"
      >
        <v-col
          v-for="variable in currentView === 'GPS' ? statCardsGPS : statCardsHR"
          :key="variable.id"
        >
          <stat-card
            :loading="advancedPerformanceStats.loading"
            :title="variable.text"
            :value="advancedPerformanceStats.data.group[variable.value]"
            :max="15000"
            :color="getColorFromValue(advancedPerformanceStats.data.group[variable.value], 15000)"
          />
        </v-col>
      </v-row>

      <section
        class="performance__tables mb-6"
      >
        <v-card class="mt-6">
          <v-card-title class="px-6 py-4">
            <v-row
              no-gutters
              align="center"
            >
              <v-col>
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('event_details.players') }}
                </div>
              </v-col>
              <v-col
                cols="auto"
                class="pl-2"
              >
                <variable-selector
                  v-if="variables.data"
                  :variables="variables.data"
                  :dashboard="dashboard"
                  :category="currentView"
                />
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text class="pa-0">
            <performance-general-table
              :player-performance="advancedPerformanceStats.data.players || []"
              :loading="advancedPerformanceStats.loading"
              :max-values="advancedPerformanceStats.maxValues"
              :table-headers="currentView === 'GPS' ? tableHeadersGPS : tableHeadersHR"
              :colors-max-values="colorMaxValuesTraining"
            />
          </v-card-text>
        </v-card>
      </section>
    </div>

    <performance-coach-customize
      :visible.sync="customizeModal"
      :dashboard="dashboard"
      :view="currentView"
      @save="fetchVariables"
    />

    <advanced-performance-filter-dialog
      :visible.sync="filtersDialog"
      :filters="advancedFilters"
      :events="events"
      :players="allPlayers"
      :exercises="exercises"
      @click:apply="fetchAdvancedPerformanceStats"
    />
  </div>
</template>

<script>
import {
  CoachApiService,
  EventApiService,
  PlayerApiService,
  PlayerPositionApiService,
  PlayerStatusApiService
} from '@/data/services'
import PerformanceGeneralTable from '@/components/performance/PerformanceGeneralTable'
import { getColorForValue, processMaxValues, toCamelCase } from '@/utils/helpers'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import { maxValuesPerformanceTraining, PerformanceTableHeaders } from '@/data/models/TableHeader'
import { orderBy } from 'lodash'
import PerformanceCoachCustomize from '@/components/performance/PerfomanceCoachCustomize.vue'
import { DashboardType } from '@/data/models/Dashboard'
import { EventTypes } from '@/data/models'
import StatCard from '@/components/StatCard.vue'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import AdvancedPerformanceFilterDialog from '@/components/dialogs/AdvancedPerformanceFilterDialog.vue'

export default {
  name: 'PerformanceAdvancedView',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    AdvancedPerformanceFilterDialog,
    VariableSelector,
    StatCard,
    PerformanceCoachCustomize,
    PerformanceGeneralTable
  },
  data: () => ({
    currentTab: 'event',
    currentView: 'GPS',
    heartRateView: false,
    eventSelection: false,
    eventSelectionFilter: undefined,
    date: dayjs().format('YYYY-MM-DD'),
    events: {
      loading: false,
      data: []
    },
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    variables: {
      data: null,
      loading: false
    },
    filterDateAdvancedActive: false,
    tableHeaders: Object.assign({}, PerformanceTableHeaders),
    advancedFilters: {
      dateRange: [],
      eventIds: [],
      teamId: 'all',
      playerIds: [],
      exerciseIds: [],
      eventType: null
    },
    filters: {
      eventType: undefined,
      eventName: ''
    },
    allPlayers: {
      loading: false,
      data: []
    },
    exercises: {
      loading: false,
      data: []
    },
    advancedPerformanceStats: {
      loading: false,
      data: {
        group: {},
        players: []
      },
      maxValues: {}
    },
    filtersDialog: false,
    customizeModal: false,
    colorMaxValuesTraining: maxValuesPerformanceTraining,
    dashboard: DashboardType.ADVANCED_PERFORMANCE
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    playersByTeam() {
      const teamId = this.currentTeam.id || this.teams[0].id

      return this.allPlayers.data.filter(p => p.team_id === teamId)
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    statCardsGPS() {
      return this.variables.data ? orderBy(this.variables.data.team.GPS.filter(tableHeader => tableHeader.selected), ['order']) : []
    },
    statCardsHR() {
      return this.variables.data ? orderBy(this.variables.data.team.HR.filter(tableHeader => tableHeader.selected), ['order']) : []
    },
    tableHeadersGPS() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.GPS.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersHR() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.HR.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    views() {
      return [
        {
          name: 'GPS',
          title: this.$t('performance.event.gps'),
          active: this.user.organisation_settings.has_gps_module
        },
        {
          name: 'HR',
          title: this.$t('performance.event.hr'),
          active: this.user.organisation_settings.has_heart_rate_module
        }
      ].filter(view => view.active)
    }
  },
  watch: {
    currentTeam() {
      this.advancedFilters = {
        dateRange: [this.$dayjs().subtract(1, 'month').format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')],
        eventIds: [],
        teamId: 'all',
        playerIds: [],
        exerciseIds: [],
        eventType: null
      }
      this.fetchAdvancedPerformanceStats()
    },
    'advancedFilters.dateRange': function () {
      this.fetchEvents()
      this.fetchTrainingExercises()
    },
    'advancedFilters.eventIds': function () {
      this.fetchTrainingExercises()
    },
    'advancedFilters.playerIds': function () {
      this.fetchTrainingExercises()
    },
    dashboard() {
      this.fetchVariables()
    }
  },
  created() {
    this.eventTypes = EventTypes

    this.mainEventTypesList = Object.keys(EventTypes).map(k => {
      return {
        ...EventTypes[k],
        key: k
      }
    }).filter(e => e.is_other === false)

    if (this.$route.query.from) {
      this.advancedFilters.dateRange = [this.$route.query.from, this.$route.query.to]
      this.advancedFilters.playerIds = [this.$route.query.player_id]

      this.$router.replace({ query: {} })
    } else {
      this.advancedFilters.dateRange = [this.$dayjs().subtract(1, 'month').format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')]
    }

    this.fetchVariables()
    this.fetchEvents()
    this.fetchAllPlayers()
    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()
    this.fetchTrainingExercises()
  },
  methods: {
    orderBy,
    fetchEvents: async function () {
      this.events.loading = true

      try {
        const response = await EventApiService.getAll({
          order_by: 'start_at',
          order: 'desc',
          has_integration: true,
          only_own_events: true,
          from: this.advancedFilters.dateRange[0],
          to: this.advancedFilters.dateRange[1],
          team_id: this.currentTeam.id,
          search: this.advancedFilters.eventName,
          type: this.advancedFilters.eventType,
          per_page: 100,
          page: 1
        })

        this.events.data = response.data
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.events.loading = false
      }
    },
    fetchEventsDebounced: debounce(function () {
      this.fetchEvents()
    }, 500),
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    },
    fetchPlayers: debounce(async function () {
      try {
        this.weeklyStatsPlayers.loading = true

        const queryParams = {
          team_id: this.currentTeam.id === 'all' ? undefined : this.currentTeam.id,
          stats_date: this.date
        }

        const response = await PlayerApiService.getAll(queryParams)

        const players = response.data.map(player => {
          if (!player.latest_stats) {
            return player
          }

          return player
        })

        this.weeklyStatsPlayers.maxValues = processMaxValues(players.map(player => player.latest_stats), 1.1)

        this.weeklyStatsPlayers.data = players
      } catch (e) {
        this.weeklyStatsPlayers.data = []
        throw e
      } finally {
        this.weeklyStatsPlayers.loading = false
      }
    }, 200),
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchAllPlayers() {
      try {
        this.allPlayers.loading = true

        const result = await PlayerApiService.getSimpleList({
          order_by: 'last_name',
          order: 'ASC'
        })

        this.allPlayers.data = result.data

        await this.$nextTick()

        await this.fetchAdvancedPerformanceStats()
      } finally {
        this.allPlayers.loading = false
      }
    },
    async fetchAdvancedPerformanceStats() {
      if (this.advancedFilters.playerIds.length === 0 && this.playersByTeam.length === 0) return

      if (this.advancedPerformanceStats.loading) return

      try {
        this.advancedPerformanceStats.loading = true

        const params = {
          player_ids: this.advancedFilters.playerIds.length === 0 ? this.playersByTeam.map(p => p.id) : this.advancedFilters.playerIds,
          event_types: this.advancedFilters.eventType ? [this.advancedFilters.eventType] : undefined,
          event_ids: this.advancedFilters.eventIds,
          date_from: this.advancedFilters.dateRange[0] ?? undefined,
          date_to: this.advancedFilters.dateRange[1] ?? undefined,
          exercise_ids: this.advancedFilters.exerciseIds
        }

        const result = await EventApiService.getAdvancedPerformanceStats(params)

        this.advancedPerformanceStats.data = result

        this.advancedPerformanceStats.maxValues = processMaxValues(result.players.map(player => player.player_stats || {}))
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_advanced_statistics'),
          color: 'error'
        })

        throw e
      } finally {
        this.advancedPerformanceStats.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        this.variables.data = await CoachApiService.getVariables(this.user.coach.id, {
          dashboard: this.dashboard
        })

        Object.keys(this.variables.data).forEach(group => {
          Object.keys(this.variables.data[group]).forEach(category => {
            this.variables.data[group][category] = this.variables.data[group][category].map(v => {
              return {
                ...v,
                text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
                long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
              }
            })
          })
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    async fetchTrainingExercises() {
      this.exercises.loading = true

      try {
        const result = await EventApiService.getAllExercisesFromEvents({
          from: this.advancedFilters.dateRange[0],
          to: this.advancedFilters.dateRange[1],
          ids: this.advancedFilters.eventIds,
          type: this.advancedFilters.eventType,
          player_ids: this.advancedFilters.playerIds
        })

        this.exercises.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_events.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.exercises.loading = false
      }
    }

  }
}
</script>

<style scoped>
</style>
